import React, { useState } from 'react';
import { Button, TwIconButton } from 'components/common';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import { FaTimes, FaTrash, FaExclamationTriangle } from 'react-icons/fa';
const DriveDeleteConfirm = ({
  show,
  onClose,
  onConfirm,
  title = 'Confirm delete',
  message = 'Are you sure you want to delete?',
  itemName,
  itemNames = [],
  confirmLabel = 'Delete',
}) => {
  const [busy, setBusy] = useState(false);

  const handleConfirm = async () => {
    setBusy(true);
    try { await onConfirm(); }
    finally { setBusy(false); }
  };

  if (!show) return null;

  const isBulk = itemNames.length > 0;

  const modal = (
    <div
      className="tw-fixed tw-inset-0 tw-flex tw-items-center tw-justify-center tw-p-4"
      style={{ zIndex: 999999, backgroundColor: 'rgba(15,23,42,0.5)' }}
      onClick={busy ? undefined : onClose}
    >
      <div
        className="tw-bg-white tw-rounded tw-shadow-xl tw-w-full tw-max-w-md tw-overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="tw-flex tw-items-center tw-justify-between tw-px-5 tw-py-3 tw-border-b tw-border-slate-100">
          <div className="tw-flex tw-items-center tw-gap-2">
            <div className="tw-w-8 tw-h-8 tw-bg-red-50 tw-text-red-600 tw-border tw-border-red-100 tw-rounded-md tw-flex tw-items-center tw-justify-center">
              <FaTrash size={12} />
            </div>
            <h3 className="tw-text-base tw-font-bold tw-text-slate-900 tw-m-0">{title}</h3>
          </div>
          <TwIconButton icon={FaTimes} size="md" variant="ghost" onClick={onClose} disabled={busy} title="Close" />
        </div>

        <div className="tw-p-5">
          <p className="tw-text-sm tw-text-slate-700 tw-font-medium tw-mb-3 tw-m-0">{message}</p>

          {!isBulk && itemName && (
            <p className="tw-mt-3 tw-text-xs tw-font-bold tw-text-slate-700 tw-bg-slate-50 tw-border tw-border-slate-200 tw-px-3 tw-py-2 tw-rounded-md tw-truncate tw-m-0">
              {itemName}
            </p>
          )}

          {isBulk && (
            <div className="tw-mt-3 tw-max-h-40 tw-overflow-y-auto tw-bg-slate-50 tw-border tw-border-slate-200 tw-rounded-md">
              {itemNames.map((nm, i) => (
                <div key={i} className="tw-px-3 tw-py-2 tw-text-xs tw-font-medium tw-text-slate-700 tw-border-b tw-border-slate-100 last:tw-border-b-0 tw-truncate">
                  {nm}
                </div>
              ))}
            </div>
          )}

          <div className="tw-mt-4 tw-flex tw-items-start tw-gap-2 tw-bg-red-50 tw-border tw-border-red-100 tw-rounded-md tw-p-2.5">
            <FaExclamationTriangle className="tw-text-red-600 tw-shrink-0 tw-mt-0.5" size={11} />
            <p className="tw-text-xs tw-font-bold tw-text-red-700 tw-m-0">This action cannot be undone.</p>
          </div>
        </div>

        <div className="tw-flex tw-items-center tw-justify-end tw-gap-2 tw-px-5 tw-py-3 tw-border-t tw-border-slate-100 tw-bg-slate-50">
          <Button variant="secondary" size="md" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button variant="danger" size="md" leftIcon={FaTrash} onClick={handleConfirm} loading={busy}>
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};

DriveDeleteConfirm.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  title: PropTypes.string,
  message: PropTypes.string,
  itemName: PropTypes.string,
  itemNames: PropTypes.arrayOf(PropTypes.string),
  confirmLabel: PropTypes.string,
};

export default DriveDeleteConfirm;
